import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import { Cpu, TestTube, Info } from 'lucide-react';
import { getStatusColor } from '../kpiUtils';

const RunwayBar = ({ label, days, maxDays = 14, status }) => {
  const pct = Math.min((days / maxDays) * 100, 100);
  const colors = getStatusColor(status);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 truncate text-slate-700 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${colors.bar}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`w-14 text-right font-medium ${colors.text}`}>
        {days >= 999 ? 'OK' : `${days}d`}
      </span>
    </div>
  );
};

const CapacityGauge = ({ value, max, label, color }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const chartData = [{ value: percentage, fill: color }];
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: 180, height: 110 }}>
        <RadialBarChart
          width={180} height={110}
          cx={90} cy={100}
          innerRadius={60} outerRadius={85}
          startAngle={180} endAngle={0}
          data={chartData}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar dataKey="value" cornerRadius={5} background={{ fill: '#f1f5f9' }} />
        </RadialBarChart>
        <div className="absolute bottom-1 left-0 right-0 text-center pointer-events-none">
          <p className="text-2xl font-bold text-slate-900 tabular-nums leading-none">{value}</p>
          <p className="text-[10px] text-slate-600 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );
};

const CapacityPanel = ({ icon: Icon, title, gauge, runways, noDataNote, onClick }) => (
  <div
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    className={`bg-white rounded-xl border border-slate-200 p-5 shadow-sm ${
      onClick ? 'cursor-pointer hover:border-slate-300 hover:shadow-md transition-all' : ''
    }`}
  >
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-slate-500" />
      <h3 className="text-base font-semibold text-slate-800">{title}</h3>
    </div>
    <div className="flex justify-center mb-4">{gauge}</div>
    {runways.length > 0 ? (
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Component runway</p>
        {runways.map((r) => <RunwayBar key={r.label} {...r} />)}
      </div>
    ) : (
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-2">
        <Info className="w-3.5 h-3.5 shrink-0" />
        <span>{noDataNote}</span>
      </div>
    )}
  </div>
);

export default function CapacityIntelligence({ data }) {
  const navigate = useNavigate();

  if (!data) return null;

  const { stats, inventoryItems, reagentRunway, limitingFactor, deviceCap } = data;

  const deviceCapacity = deviceCap?.max_capacity ?? stats.hardware_sets_capacity ?? 0;
  const deviceMax = Math.max(deviceCapacity * 3, 50);
  const testMax = Math.max(stats.testCapacity * 2, 200);

  const deviceColor = deviceCapacity >= 20 ? '#10b981' : deviceCapacity >= 10 ? '#f59e0b' : '#ef4444';
  const testColor = stats.testCapacity >= 100 ? '#10b981' : stats.testCapacity >= 50 ? '#f59e0b' : '#ef4444';

  const reagentRunways = (() => {
    if (reagentRunway && Object.keys(reagentRunway).length > 0) {
      return Object.entries(reagentRunway)
        .sort(([, a], [, b]) => a - b)
        .slice(0, 5)
        .map(([name, days]) => ({
          label: name, days,
          status: days <= 5 ? 'critical' : days <= 20 ? 'warning' : 'healthy',
        }));
    }
    if (inventoryItems.length > 0) {
      return inventoryItems
        .filter((i) => i.category === 'reagent')
        .sort((a, b) => (a.days_remaining ?? 999) - (b.days_remaining ?? 999))
        .slice(0, 5)
        .map((i) => ({
          label: i.name,
          days: i.days_remaining ?? 999,
          status: i.status || (i.days_remaining <= 2 ? 'critical' : i.days_remaining <= 7 ? 'warning' : 'healthy'),
        }));
    }
    return [];
  })();

  const hwRunways = inventoryItems
    .filter((i) => i.category === 'hardware')
    .sort((a, b) => (a.days_remaining ?? 999) - (b.days_remaining ?? 999))
    .slice(0, 4)
    .map((i) => ({
      label: i.name,
      days: i.days_remaining ?? 999,
      status: i.status || (i.days_remaining <= 2 ? 'critical' : i.days_remaining <= 7 ? 'warning' : 'healthy'),
    }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CapacityPanel
          icon={Cpu}
          title="Device Manufacturing"
          gauge={<CapacityGauge value={deviceCapacity} max={deviceMax} label="devices buildable" color={deviceColor} />}
          runways={hwRunways}
          noDataNote="No hardware component detail available"
          onClick={() => navigate('/lab/electronic-components')}
        />
        <CapacityPanel
          icon={TestTube}
          title="Soil Test Capacity"
          gauge={<CapacityGauge value={stats.testCapacity} max={testMax} label="tests possible" color={testColor} />}
          runways={reagentRunways}
          noDataNote="No reagent runway data available"
          onClick={() => navigate('/lab/stock')}
        />
      </div>

      {limitingFactor && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/80 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-amber-200/80 bg-amber-100/60">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-900">
              Manufacturing Bottleneck Alert
            </p>
            <p className="text-[11px] text-amber-800 mt-0.5">
              Which component part is limiting how many devices you can still build from inventory
            </p>
          </div>
          <div className="flex items-start gap-3 p-4">
            <span className="text-amber-500 shrink-0 text-lg leading-none">⚠</span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1">
                Limiting component
              </p>
              <p className="font-semibold text-amber-950 text-sm">{limitingFactor.name}</p>
              <p className="text-xs text-amber-900 mt-2">
                <span className="font-medium">{limitingFactor.current_stock}</span> unit(s) in stock → max{' '}
                <strong>{limitingFactor.max_units_possible}</strong> more device(s) buildable from parts
              </p>
              {deviceCap?.limiting_component && deviceCap.limiting_component !== limitingFactor.name && (
                <p className="text-xs text-amber-800 mt-1.5 pt-1.5 border-t border-amber-200/80">
                  <span className="font-semibold">Device-specific bottleneck:</span> {deviceCap.limiting_component}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
